import React, { useContext, useState } from 'react';
import {
  Box,
  Text,
  Input,
  VStack,
  HStack,
  Button,
  IconButton,
  Flex,
  useToast,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { FaPlus, FaMinus } from 'react-icons/fa';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { cashCap, handleIncrement, handleDecrement } from '../../utils';
import { Bundle } from '@foodfluence/shared';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import ModalShell from '../../../../components/ui/ModalShell';
import { ff } from '../../../../theme';

const AddBundleModal: React.FC<{ 
  isOpen: boolean, 
  onClose: () => void, 
  onBundleCreate: (bundle: Bundle) => void 
}> = ({ isOpen, onClose, onBundleCreate }) => {
  const toast = useToast();
  const { influencerData } = useContext(InfluencerDataContext);
  
  const [bundleName, setBundleName] = useState('');
  const [tiktokCount, setTiktokCount] = useState(0);
  const [igReelCount, setIgReelCount] = useState(0);
  const [igPostCount, setIgPostCount] = useState(0);
  const [igStoryCount, setIgStoryCount] = useState(0);
  const [cashAmount, setCashAmount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async () => {
    try {
      const bundle: Bundle = {
        name: bundleName,
        deliverableCounts: {
          tiktoks: tiktokCount, 
          instagramReels: igReelCount,
          instagramPosts: igPostCount,
          instagramStories: igStoryCount,
        },
        cash: Number(cashAmount)
      };
      onBundleCreate(bundle);
      toast({
        title: "Bundle added",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      // Reset form
      setBundleName('');
      setTiktokCount(0);
      setIgReelCount(0);
      setIgPostCount(0);
      setIgStoryCount(0);
      setCashAmount(0);
      setErrorMessage(null);
      
      onClose();
    } catch (error) {
      console.error('Error adding bundle:', error);
      toast({
        title: "Error adding bundle",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const isSubmitEnabled = 
    bundleName.trim() !== '' &&
    (tiktokCount > 0 || igReelCount > 0 || igPostCount > 0 || igStoryCount > 0) && 
    (cashAmount > 0) && 
    cashAmount <= cashCap;

  const handleCashChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (!isNaN(value)) {
      setCashAmount(value);
      if (value > cashCap) {
        setErrorMessage(`Cash amount cannot exceed ${cashCap.toLocaleString()}.`);
      } else {
        setErrorMessage(null);
      }
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="New bundle"
      footer={
        // Sticky so Save stays reachable while the form scrolls; the dialog container is the scroller.
        <Flex
          position="sticky"
          bottom={0}
          gap={3}
          mt={5}
          mx={-5}
          px={5}
          pt="14px"
          pb="env(safe-area-inset-bottom)"
          bg="white"
          borderTop={`1px solid ${ff.line}`}
        >
          <Button variant="inkOutline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="lime" flex={1} onClick={handleSubmit} isDisabled={!isSubmitEnabled}>
            Save
          </Button>
        </Flex>
      }
    >
      <VStack spacing={6} align="stretch">
        <Box>
          <Text fontSize="14px" fontWeight={600} color={ff.ink} mb="6px">
            Bundle name
          </Text>
          <Input
            value={bundleName}
            onChange={(e) => setBundleName(e.target.value)}
            placeholder="e.g. Starter Package"
            fontSize="16px"
          />
        </Box>

        <Box>
          <Text textStyle="eyebrow" color={ff.text2} mb={3}>
            Content types
          </Text>
          <Box border="1px solid" borderColor={ff.line} borderRadius="20px" overflow="hidden">
            {[
              {
                label: 'Instagram Reel',
                count: igReelCount,
                setCount: setIgReelCount,
                icon: <FaInstagram size={20} />,
              },
              {
                label: 'Instagram Post',
                count: igPostCount,
                setCount: setIgPostCount,
                icon: <FaInstagram size={20} />,
              },
              {
                label: 'Instagram Story',
                count: igStoryCount,
                setCount: setIgStoryCount,
                icon: <FaInstagram size={20} />,
              },
              {
                label: 'TikTok Video',
                count: tiktokCount,
                setCount: setTiktokCount,
                icon: <FaTiktok size={18} />,
              },
            ].map(({ label, count, setCount, icon }, idx) => (
              <Flex
                key={label}
                px={4}
                minH="60px"
                align="center"
                justify="space-between"
                borderTop={idx === 0 ? 'none' : '1px solid'}
                borderColor={ff.line}
              >
                <HStack spacing={3}>
                  <Box color={ff.ink}>{icon}</Box>
                  <Text fontSize="15px" fontWeight={500} color={ff.ink}>
                    {label}
                  </Text>
                </HStack>
                <HStack spacing={1} bg={ff.grey} borderRadius="full" p="2px">
                  <IconButton
                    icon={<FaMinus />}
                    aria-label={`Decrease ${label}`}
                    onClick={() => handleDecrement(setCount, count)}
                    w="40px"
                    h="40px"
                    minW="40px"
                    fontSize="12px"
                    variant="ghost"
                    isDisabled={count === 0}
                    color={ff.ink}
                    borderRadius="full"
                    _hover={{ bg: 'white' }}
                  />
                  <Text fontFamily="display" fontSize="16px" fontWeight={600} minW="20px" textAlign="center" color={ff.ink}>
                    {count}
                  </Text>
                  <IconButton
                    icon={<FaPlus />}
                    aria-label={`Increase ${label}`}
                    onClick={() => handleIncrement(setCount, count)}
                    w="40px"
                    h="40px"
                    minW="40px"
                    fontSize="12px"
                    variant="ghost"
                    color={ff.ink}
                    borderRadius="full"
                    _hover={{ bg: 'white' }}
                  />
                </HStack>
              </Flex>
            ))}
          </Box>
        </Box>

        <Box>
          <Text fontSize="14px" fontWeight={600} color={ff.ink} mb="6px">
            Bundle price
          </Text>
          <InputGroup>
            <InputLeftElement pointerEvents="none" color={ff.text2} fontSize="18px" h="48px">
              $
            </InputLeftElement>
            <Input
              type="number"
              value={cashAmount || ''}
              onChange={handleCashChange}
              placeholder="0"
              fontSize="18px"
              fontWeight={500}
              pl={8}
              isInvalid={Boolean(errorMessage)}
            />
          </InputGroup>

          {errorMessage && (
            <Text color={ff.danger} fontSize="13px" mt={2}>
              {errorMessage}
            </Text>
          )}
        </Box>
      </VStack>
    </ModalShell>
  );
};

export default AddBundleModal;
